import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { refreshToken } from '../../../../Redux/Auth/authActions';
import { partnerPortalApi } from '../Services/partnerPortalApi';
import { resolveAccessToken, usePartnerAccessToken } from './usePartnerAccessToken';
import {
  normalizePartnerCompaniesList,
  normalizePartnerCompanyRecord,
} from '../Utils/partnerCompanyUsersUtils';

const isUnauthorizedError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return message.includes('unauthorized') || message.includes('authentication');
};

export const normalizePartnerCompaniesResponse = (data) => {
  if (Array.isArray(data)) {
    return normalizePartnerCompaniesList(data);
  }

  if (Array.isArray(data?.companies)) {
    return normalizePartnerCompaniesList(data.companies);
  }

  if (Array.isArray(data?.results)) {
    return normalizePartnerCompaniesList(data.results);
  }

  return [];
};

export const usePartnerCompanies = (enabled = true) => {
  const dispatch = useDispatch();
  const accessToken = usePartnerAccessToken();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const withAuthRetry = useCallback(async (apiCall) => {
    const token = resolveAccessToken(accessToken);
    if (!token) {
      throw new Error('Your session has expired. Please sign in again.');
    }

    try {
      return await apiCall(token);
    } catch (err) {
      if (!isUnauthorizedError(err) || !Cookies.get('refreshToken')) {
        throw err;
      }

      await dispatch(refreshToken());
      const refreshedToken = Cookies.get('accessToken');
      if (!refreshedToken) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      return apiCall(refreshedToken);
    }
  }, [accessToken, dispatch]);

  const refreshCompanies = useCallback(async () => {
    if (!enabled) {
      setCompanies([]);
      setLoading(false);
      return;
    }

    const token = resolveAccessToken(accessToken);
    if (!token) {
      setCompanies([]);
      setLoading(false);
      setError('Your session has expired. Please sign in again.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await withAuthRetry((activeToken) => (
        partnerPortalApi.fetchPartnerCompanies(activeToken)
      ));
      setCompanies(normalizePartnerCompaniesResponse(data));
    } catch (err) {
      setError(err.message);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, enabled, withAuthRetry]);

  const updateCompany = useCallback(async (companyId, payload) => {
    setSaving(true);

    try {
      const result = await withAuthRetry((activeToken) => (
        partnerPortalApi.patchPartnerCompany(activeToken, companyId, payload)
      ));

      const updatedCompany = result?.company || result;
      if (updatedCompany?.id || updatedCompany?._id) {
        const normalizedCompany = normalizePartnerCompanyRecord(updatedCompany);
        setCompanies((current) => current.map((company) => (
          company.id === normalizedCompany.id ? { ...company, ...normalizedCompany } : company
        )));
      } else {
        await refreshCompanies();
      }

      return updatedCompany;
    } finally {
      setSaving(false);
    }
  }, [refreshCompanies, withAuthRetry]);

  const createCompany = useCallback(async (payload) => {
    setSaving(true);

    try {
      const result = await withAuthRetry((activeToken) => (
        partnerPortalApi.createPartnerCompany(activeToken, payload)
      ));
      await refreshCompanies();
      return result?.company || result;
    } finally {
      setSaving(false);
    }
  }, [refreshCompanies, withAuthRetry]);

  useEffect(() => {
    refreshCompanies();
  }, [refreshCompanies]);

  return {
    companies,
    loading,
    error,
    saving,
    refreshCompanies,
    updateCompany,
    createCompany,
  };
};
